import React, { useEffect, useRef, useState } from "react";
import {
  Camera, Image as ImageIcon, Sparkles, Upload, X, Lock, Play,
  Users as UsersIcon, Shield, Fan, Clover, Video as VideoIcon,
  Baby, Gift, Music, Cake, PartyPopper, Flower2, Eye, EyeOff, Share2, Plus, Trash2, Bell,
  ChevronLeft, ChevronRight, Download, Globe, Heart
} from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import { useSurpriseNotif } from "@/lib/surpriseNotif";
import { useClock } from "@/lib/clock";
import { api, formatApiError, API_BASE } from "@/lib/api";
import { getGuestName, setGuestName } from "@/lib/guest";
import { tContent } from "@/lib/content-i18n";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import Video360Player from "@/components/wedding/Video360Player";

const MAX_BYTES = 25 * 1024 * 1024;

const SURPRISE_ICONS = {
  fan: Fan,
  image: ImageIcon,
  clover: Clover,
  sparkles: Sparkles,
  video: VideoIcon,
  baby: Baby,
  gift: Gift,
  music: Music,
  cake: Cake,
  party: PartyPopper,
  rose: Flower2,
  camera: Camera,
};

export default function Galeria() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [tab, setTab] = useState("invitados");
  const navigate = useNavigate();

  return (
    <main className="section" data-testid="galeria-page">
      <p className="eyebrow text-center mb-1">{t.galeria.eyebrow}</p>
      <h1 className="page-title font-script">{t.galeria.title}</h1>

      {/* Modo novios indicator */}
      <div className="flex justify-center mb-6">
        {user ? (
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[color:var(--ink-wine)] text-white text-xs tracking-[0.2em] uppercase font-semibold" data-testid="couple-mode-active">
            <Lock size={12} /> {t.galeria.couplePanelActive}
          </div>
        ) : (
          <button
            onClick={() => navigate("/admin/login")}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[color:var(--line)] text-[color:var(--ink-wine)] text-xs tracking-[0.2em] uppercase font-semibold hover:border-[color:var(--ink-wine)]"
            data-testid="couple-mode-button"
          >
            <Lock size={12} /> {t.galeria.couplePanel}
          </button>
        )}
      </div>

      <div className="pill-group max-w-xl mx-auto mb-8" data-testid="gallery-tabs">
        <button onClick={() => setTab("invitados")} className={`pill ${tab === "invitados" ? "active" : ""}`} data-testid="gtab-invitados">
          <UsersIcon size={14} /> {t.galeria.tabGuests}
        </button>
        <button onClick={() => setTab("profesionales")} className={`pill ${tab === "profesionales" ? "active" : ""}`} data-testid="gtab-pros">
          <Camera size={14} /> {t.galeria.tabPros}
        </button>
        <button onClick={() => setTab("sorpresa")} className={`pill ${tab === "sorpresa" ? "active" : ""}`} data-testid="gtab-surprises">
          <Sparkles size={14} /> {t.galeria.tabSurprises}
        </button>
      </div>

      {tab === "invitados" && <GuestsGallery t={t} />}
      {tab === "profesionales" && <ProGallery t={t} />}
      {tab === "sorpresa" && <SurprisesGallery t={t} />}
    </main>
  );
}

// --- Media helpers ---
function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onerror = reject;
    fr.onload = () => resolve(fr.result);
    fr.readAsDataURL(file);
  });
}

async function resizeImage(file) {
  const dataUrl = await readFileAsDataUrl(file);
  const img = new Image();
  await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = dataUrl; });
  const maxSide = 1600;
  let { width, height } = img;
  if (width > maxSide || height > maxSide) {
    const r = Math.min(maxSide / width, maxSide / height);
    width = Math.round(width * r); height = Math.round(height * r);
  }
  const c = document.createElement("canvas");
  c.width = width; c.height = height;
  c.getContext("2d").drawImage(img, 0, 0, width, height);
  return c.toDataURL("image/jpeg", 0.82);
}

function validateVideo(file, t) {
  return new Promise((resolve, reject) => {
    if (file.size > MAX_BYTES) return reject(new Error(t.common.videoTooBig));
    const v = document.createElement("video");
    v.preload = "metadata";
    v.onloadedmetadata = () => {
      if (v.duration > 31) reject(new Error(t.common.videoTooLong));
      else resolve();
    };
    v.onerror = () => reject(new Error(t.common.invalidVideo));
    v.src = URL.createObjectURL(file);
  });
}

// Resolve the media URL for a gallery item. New items → object storage endpoint.
// Legacy items (still in DB) fall back to the embedded base64 string.
export function mediaUrl(item, opts = {}) {
  if (!item) return "";
  if (item.storage_path || item.file_size || (!item.media_base64 && item.id)) {
    const params = [];
    if (opts.download) params.push("download=true");
    if (opts.thumb) params.push("thumb=1");
    const qs = params.length ? `?${params.join("&")}` : "";
    return `${API_BASE}/gallery/file/${item.id}${qs}`;
  }
  return item.media_base64 || "";
}

function MediaGrid({ items, onOpen, testid, onDelete, paginate = false, pageSize = 60, loadMoreLabel = "Cargar más", favoritesOnly = false, resetKey }) {
  // stats[id] = { likes: [...names], count, downloads }
  const [stats, setStats] = useState({});
  const guestName = getGuestName() || "Anónimo";

  // Prefetch stats for ALL items when the favourite filter is on so we know which
  // to keep; otherwise only fetch stats for what's currently visible.
  useEffect(() => {
    const targetIds = (favoritesOnly ? items : items).map((v) => v.id);
    const missing = targetIds.filter((id) => !(id in stats));
    if (missing.length === 0) return;
    let cancelled = false;
    (async () => {
      for (let i = 0; i < missing.length; i += 200) {
        const chunk = missing.slice(i, i + 200);
        try {
          const { data } = await api.post("/gallery/stats", { ids: chunk });
          if (cancelled) return;
          setStats((s) => ({ ...s, ...data }));
        } catch { /* stats are non-critical */ }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, favoritesOnly]);

  const filteredItems = favoritesOnly
    ? items.filter((it) => (stats[it.id]?.count || 0) > 0)
    : items;

  const [visibleCount, setVisibleCount] = useState(pageSize);
  // Only reset pagination when the parent explicitly signals a fresh context
  // (folder switch → resetKey changes) or when the favourites filter is toggled.
  // Deletions/uploads shouldn't reset the scroll depth.
  useEffect(() => { setVisibleCount(pageSize); }, [resetKey, pageSize, favoritesOnly]);
  const visible = paginate ? filteredItems.slice(0, visibleCount) : filteredItems;
  const hasMore = paginate && filteredItems.length > visibleCount;

  const toggleLike = async (id) => {
    const cur = stats[id] || { likes: [], count: 0, downloads: 0 };
    const already = cur.likes.includes(guestName);
    // Optimistic update
    const optimistic = {
      ...cur,
      likes: already ? cur.likes.filter((n) => n !== guestName) : [...cur.likes, guestName],
    };
    optimistic.count = optimistic.likes.length;
    setStats((s) => ({ ...s, [id]: optimistic }));
    try {
      const { data } = await api.post(`/gallery/${id}/like`, { name: guestName });
      setStats((s) => ({ ...s, [id]: { ...(s[id] || {}), likes: data.likes, count: data.count } }));
    } catch (e) {
      // Rollback on error
      setStats((s) => ({ ...s, [id]: cur }));
      toast.error(formatApiError(e));
    }
  };

  const trackDownload = (id) => {
    // Fire-and-forget
    api.post(`/gallery/${id}/download`).then(({ data }) => {
      setStats((s) => ({ ...s, [id]: { ...(s[id] || { likes: [], count: 0 }), downloads: data.downloads } }));
    }).catch(() => {});
  };

  return (
    <div>
    {favoritesOnly && filteredItems.length === 0 ? (
      <div className="s-card s-card-pad text-center max-w-xl mx-auto" data-testid={`${testid}-favorites-empty`}>
        <Heart className="mx-auto text-[color:var(--ink-wine)] mb-3" size={36} />
        <p className="font-script-alt text-2xl text-[color:var(--ink-wine)] mb-1">Aún no hay favoritos</p>
        <p className="text-sm text-[color:var(--ink-soft)]">Da corazón a las fotos y vídeos que más te gusten para verlos aquí.</p>
      </div>
    ) : (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid={testid}>
      {visible.map((it, idx) => {
        const st = stats[it.id] || { likes: [], count: 0, downloads: 0 };
        const liked = st.likes.includes(guestName);
        return (
        <div key={it.id} className="group relative">
          <button onClick={() => onOpen(idx)} className="block w-full" data-testid={`media-${it.id}`}>
            <div className="s-card overflow-hidden relative">
              {it.media_type === "video" ? (
                <>
                  {/* If a thumb was extracted from the video, show it. Otherwise fall back to
                      a solid tile with a play icon. `preload=none` is implicit since we never
                      mount a <video> here — the actual player only loads inside the viewer. */}
                  <div className="w-full aspect-[4/3] bg-[color:var(--bg-blush)] flex items-center justify-center relative overflow-hidden">
                    {it.thumb_path && (
                      <img
                        src={mediaUrl(it, { thumb: true })}
                        alt={it.caption || it.author}
                        loading="lazy"
                        decoding="async"
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                    )}
                    <div className="absolute inset-0 flex items-center justify-center bg-black/10">
                      <span className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-white/90 shadow-lg">
                        <Play className="text-[color:var(--ink-wine)]" size={26} fill="currentColor" />
                      </span>
                    </div>
                    {it.is_360 && (
                      <span className="absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[color:var(--ink-wine)] text-white text-[0.6rem] font-bold tracking-[0.18em] uppercase" data-testid={`badge-360-${it.id}`}>
                        <Globe size={10} /> 360º
                      </span>
                    )}
                  </div>
                </>
              ) : (
                <div className="w-full aspect-[4/3] overflow-hidden bg-[color:var(--bg-blush)]">
                  <img
                    src={mediaUrl(it, { thumb: true })}
                    alt={it.caption || it.author}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover block transition-transform duration-500 group-hover:scale-[1.02]"
                  />
                </div>
              )}
              <div className="px-3 py-2 text-left">
                {it.caption && <p className="text-xs text-[color:var(--ink-deep)] line-clamp-2 italic">{it.caption}</p>}
                <p className="text-[0.65rem] tracking-[0.18em] uppercase font-semibold text-[color:var(--ink-wine)] mt-1">{it.author}</p>
              </div>
            </div>
          </button>

          {/* Like button (bottom-left). Names appear on hover of the count. */}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); toggleLike(it.id); }}
            title={st.likes.length ? st.likes.join(", ") : "Sé el primero en dar corazón"}
            className={`absolute bottom-11 left-2 inline-flex items-center gap-1 px-2 h-7 rounded-full shadow-md text-[0.7rem] font-semibold transition ${liked ? "bg-[color:var(--ink-wine)] text-white" : "bg-white/95 text-[color:var(--ink-wine)] hover:bg-[color:var(--bg-blush)]"}`}
            data-testid={`media-like-${it.id}`}
            aria-label="Me gusta"
          >
            <Heart size={13} fill={liked ? "currentColor" : "none"} />
            {st.count > 0 && <span data-testid={`like-count-${it.id}`}>{st.count}</span>}
          </button>

          <a
            href={mediaUrl(it, { download: true })}
            download
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => { e.stopPropagation(); trackDownload(it.id); }}
            className="absolute bottom-11 right-2 inline-flex items-center gap-1 px-2 h-7 rounded-full bg-white/95 text-[color:var(--ink-wine)] opacity-0 group-hover:opacity-100 focus:opacity-100 transition shadow-md hover:bg-[color:var(--bg-blush)] text-[0.7rem] font-semibold"
            data-testid={`media-download-${it.id}`}
            aria-label="Descargar"
            title="Descargar"
          >
            <Download size={13} />
            {st.downloads > 0 && <span data-testid={`download-count-${it.id}`}>{st.downloads}</span>}
          </a>
          {onDelete && (
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(it.id); }}
              className="absolute top-2 right-2 p-1.5 rounded-full bg-white/95 text-red-700 opacity-0 group-hover:opacity-100 transition shadow-md"
              data-testid={`media-delete-${it.id}`}
              aria-label="Eliminar"
            >
              <Trash2 size={14} />
            </button>
          )}
        </div>
        );
      })}
    </div>
    )}
    {hasMore && (
      <div className="flex justify-center mt-6">
        <button
          type="button"
          onClick={() => setVisibleCount((n) => n + pageSize)}
          className="btn-outline"
          data-testid={`${testid}-load-more`}
        >
          {loadMoreLabel} ({filteredItems.length - visibleCount})
        </button>
      </div>
    )}
    </div>
  );
}

function Viewer({ items, index, onClose, onChange }) {
  const startX = React.useRef(null);
  React.useEffect(() => {
    if (index == null) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") onChange(Math.max(0, index - 1));
      else if (e.key === "ArrowRight") onChange(Math.min(items.length - 1, index + 1));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index, items.length, onClose, onChange]);

  if (index == null || !items[index]) return null;
  const item = items[index];
  const canPrev = index > 0;
  const canNext = index < items.length - 1;

  const onTouchStart = (e) => { startX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (startX.current == null) return;
    const dx = e.changedTouches[0].clientX - startX.current;
    if (dx > 50 && canPrev) onChange(index - 1);
    else if (dx < -50 && canNext) onChange(index + 1);
    startX.current = null;
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/90 flex items-center justify-center" onClick={onClose} data-testid="viewer">
      <button
        className="absolute top-4 right-4 z-10 w-11 h-11 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center backdrop-blur"
        onClick={onClose}
        aria-label="Cerrar"
        data-testid="viewer-close"
      >
        <X size={22} />
      </button>

      {canPrev && (
        <button
          className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center backdrop-blur"
          onClick={(e) => { e.stopPropagation(); onChange(index - 1); }}
          aria-label="Anterior"
          data-testid="viewer-prev"
        >
          <ChevronLeft size={26} />
        </button>
      )}

      {canNext && (
        <button
          className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center backdrop-blur"
          onClick={(e) => { e.stopPropagation(); onChange(index + 1); }}
          aria-label="Siguiente"
          data-testid="viewer-next"
        >
          <ChevronRight size={26} />
        </button>
      )}

      <div className="absolute top-4 left-1/2 -translate-x-1/2 text-white/80 text-xs tracking-[0.2em] font-semibold">
        {index + 1} / {items.length}
      </div>

      <a
        href={mediaUrl(item, { download: true })}
        download
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => { e.stopPropagation(); api.post(`/gallery/${item.id}/download`).catch(() => {}); }}
        className="absolute top-4 right-20 z-10 inline-flex items-center gap-1.5 px-3 h-11 rounded-full bg-white/15 hover:bg-white/25 text-white text-xs font-semibold tracking-[0.18em] uppercase backdrop-blur"
        data-testid="viewer-download"
        aria-label="Descargar"
      >
        <Download size={16} /> <span className="hidden sm:inline">Descargar</span>
      </a>

      <div
        className="max-w-[95vw] max-h-[90vh] flex flex-col items-center px-4"
        onClick={(e) => e.stopPropagation()}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        {item.media_type === "video" ? (
          item.is_360 ? (
            <Video360Player src={mediaUrl(item)} />
          ) : (
            <video src={mediaUrl(item)} controls autoPlay playsInline preload="metadata" className="max-h-[78vh] max-w-full rounded-lg" />
          )
        ) : (
          <img src={mediaUrl(item)} alt={item.caption || item.author} className="max-h-[78vh] max-w-full rounded-lg" />
        )}
        <div className="text-center text-white mt-3">
          {item.caption && <p className="italic text-base sm:text-lg mb-1">{item.caption}</p>}
          <p className="text-[0.65rem] tracking-[0.3em] uppercase opacity-80">— {item.author}</p>
        </div>
      </div>
    </div>
  );
}

async function shareCurrent(t) {
  const url = window.location.href;
  try {
    if (navigator.share) {
      await navigator.share({ title: t.common.sharedTitle, url });
    } else {
      await navigator.clipboard.writeText(url);
      toast.success(t.galeria.qrCopied);
    }
  } catch {/* user cancelled */}
}

// --- Guests ---
function GuestsGallery({ t }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [viewerIdx, setViewerIdx] = useState(null);
  const [favoritesOnly, setFavoritesOnly] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/gallery", { params: { category: "invitados" } }); setItems(data); } catch {}
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm(t.common.confirmDelete)) return;
    try { await api.delete(`/gallery/${id}`); toast.success(t.common.deleted); load(); } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row gap-2 justify-center mb-6 max-w-md mx-auto">
        <button onClick={() => setOpen(true)} className="btn-primary" data-testid="gallery-upload-btn">
          <Camera size={16} /> {t.galeria.uploadBtn}
        </button>
        <button onClick={() => shareCurrent(t)} className="btn-outline" data-testid="gallery-qr-btn">
          <Share2 size={14} /> {t.galeria.qrShare}
        </button>
      </div>

      {items.length === 0 ? (
        <div className="s-card s-card-pad text-center max-w-xl mx-auto" data-testid="gallery-empty-guests">
          <ImageIcon className="mx-auto text-[color:var(--ink-wine)] mb-3" size={42} />
          <p className="font-script-alt text-3xl text-[color:var(--ink-wine)] mb-2">{t.galeria.emptyGuests}</p>
          <p className="text-[color:var(--ink-soft)]">{t.galeria.emptyGuestsDesc}</p>
        </div>
      ) : (
        <>
          <div className="flex justify-center mb-6">
            <button
              type="button"
              onClick={() => setFavoritesOnly((v) => !v)}
              className={`inline-flex items-center gap-2 px-4 h-9 rounded-full text-xs font-semibold tracking-[0.18em] uppercase transition border ${favoritesOnly ? "bg-[color:var(--ink-wine)] text-white border-[color:var(--ink-wine)]" : "border-[color:var(--line)] text-[color:var(--ink-wine)] hover:border-[color:var(--ink-wine)]"}`}
              data-testid="favorites-toggle-guests"
              aria-pressed={favoritesOnly}
            >
              <Heart size={13} fill={favoritesOnly ? "currentColor" : "none"} />
              {favoritesOnly ? "Mostrando favoritos" : "Solo con corazón"}
            </button>
          </div>
          <MediaGrid
            items={items}
            onOpen={setViewerIdx}
            testid="gallery-grid-guests"
            onDelete={user ? remove : null}
            paginate={true}
            pageSize={60}
            loadMoreLabel={t.galeria.loadMore}
            favoritesOnly={favoritesOnly}
            resetKey="guests"
          />
        </>
      )}

      {open && <UploadModal t={t} onClose={() => setOpen(false)} onSaved={load} adminMode={false} category="invitados" />}
      <Viewer items={items} index={viewerIdx} onClose={() => setViewerIdx(null)} onChange={setViewerIdx} />
    </div>
  );
}

// --- Pros: 4 folders (Ceremonia, Aperitivo, Convite, Fiesta) ---
const PRO_FOLDERS_DEF = [
  { id: "ceremonia", label: "Ceremonia", labelCa: "Cerimònia", icon: "💒" },
  { id: "aperitivo", label: "Aperitivo", labelCa: "Aperitiu", icon: "🥂" },
  { id: "convite", label: "Convite", labelCa: "Convit", icon: "🍽️" },
  { id: "fiesta", label: "Fiesta", labelCa: "Festa", icon: "💃" },
];

function ProGallery({ t }) {
  const { user } = useAuth();
  const { lang } = useI18n();
  const [openFolder, setOpenFolder] = useState(null); // null = folder list, else folder id
  const [counts, setCounts] = useState({ ceremonia: 0, aperitivo: 0, convite: 0, fiesta: 0, unfiled: 0 });

  const loadCounts = async () => {
    try { const { data } = await api.get("/gallery/folders/counts"); setCounts(data || {}); } catch {}
  };
  useEffect(() => { loadCounts(); }, []);

  if (openFolder) {
    const folder = PRO_FOLDERS_DEF.find((f) => f.id === openFolder);
    return (
      <ProFolderView
        t={t}
        lang={lang}
        folder={folder}
        onBack={() => { setOpenFolder(null); loadCounts(); }}
        isAdmin={!!user}
      />
    );
  }

  return (
    <div data-testid="pro-folders-view">
      <p className="text-center text-[color:var(--ink-soft)] mb-6 text-sm">
        {t.galeria.proFoldersHint}
      </p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 max-w-4xl mx-auto">
        {PRO_FOLDERS_DEF.map((f) => (
          <button
            key={f.id}
            onClick={() => setOpenFolder(f.id)}
            className="s-card s-card-pad text-center transition-all hover:shadow-lg hover:-translate-y-0.5 group"
            data-testid={`pro-folder-${f.id}`}
          >
            <div className="text-4xl sm:text-5xl mb-2 transition-transform group-hover:scale-110">{f.icon}</div>
            <h3 className="font-script-alt text-2xl text-[color:var(--ink-wine)] mb-1">
              {lang === "ca" ? f.labelCa : f.label}
            </h3>
            <p className="text-xs tracking-[0.18em] uppercase font-bold text-[color:var(--olive-deep)]">
              {counts[f.id] || 0} {(counts[f.id] || 0) === 1 ? t.galeria.singleCountShort : t.galeria.pluralCountShort}
            </p>
          </button>
        ))}
      </div>
      {user && counts.unfiled > 0 && (
        <p className="text-center text-xs text-[color:var(--ink-soft)] mt-6 italic">
          {t.galeria.unfiledHint.replace("{n}", counts.unfiled)}
        </p>
      )}
    </div>
  );
}

function ProFolderView({ t, lang, folder, onBack, isAdmin }) {
  const [items, setItems] = useState([]);
  const [viewerIdx, setViewerIdx] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [favoritesOnly, setFavoritesOnly] = useState(false);

  // Load the folder contents. `resetPagination` decides whether to snap the view back
  // to the first 60 items. MediaGrid handles the reset internally when items change.
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/gallery", { params: { category: "profesionales", folder: folder.id } });
      setItems(data || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [folder.id]);

  const remove = async (id) => {
    if (!window.confirm(t.common.confirmDelete)) return;
    try { await api.delete(`/gallery/${id}`); toast.success(t.common.deleted); load(false); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid={`pro-folder-view-${folder.id}`}>
      <div className="flex items-center justify-between gap-3 mb-4 max-w-4xl mx-auto">
        <button onClick={onBack} className="btn-outline !px-3" data-testid="pro-folder-back">
          <ChevronLeft size={14} /> {t.galeria.backToFolders}
        </button>
        <div className="text-center flex-1">
          <p className="font-script-alt text-3xl text-[color:var(--ink-wine)] leading-none">
            {folder.icon} {lang === "ca" ? folder.labelCa : folder.label}
          </p>
          <p className="text-[0.65rem] tracking-[0.28em] uppercase font-bold text-[color:var(--ink-soft)] mt-1">
            {items.length} {items.length === 1 ? t.galeria.singleCountShort : t.galeria.pluralCountShort}
          </p>
        </div>
        {isAdmin ? (
          <button onClick={() => setOpen(true)} className="btn-primary !px-3" data-testid={`pro-upload-btn-${folder.id}`}>
            <Plus size={14} /> <span className="hidden sm:inline">{t.galeria.proUploadShort}</span>
          </button>
        ) : (
          <div className="w-[80px]" />
        )}
      </div>

      {items.length > 0 && (
        <div className="flex justify-center mb-6">
          <button
            type="button"
            onClick={() => setFavoritesOnly((v) => !v)}
            className={`inline-flex items-center gap-2 px-4 h-9 rounded-full text-xs font-semibold tracking-[0.18em] uppercase transition border ${favoritesOnly ? "bg-[color:var(--ink-wine)] text-white border-[color:var(--ink-wine)]" : "border-[color:var(--line)] text-[color:var(--ink-wine)] hover:border-[color:var(--ink-wine)]"}`}
            data-testid={`favorites-toggle-${folder.id}`}
            aria-pressed={favoritesOnly}
          >
            <Heart size={13} fill={favoritesOnly ? "currentColor" : "none"} />
            {favoritesOnly ? "Mostrando favoritos" : "Solo con corazón"}
          </button>
        </div>
      )}

      {loading ? (
        <p className="text-center text-[color:var(--ink-soft)] py-12">…</p>
      ) : items.length === 0 ? (
        <div className="s-card s-card-pad text-center max-w-xl mx-auto">
          <Camera className="mx-auto text-[color:var(--ink-wine)] mb-3" size={42} />
          <p className="font-script-alt text-2xl text-[color:var(--ink-wine)] mb-2">{t.galeria.folderEmpty}</p>
          <p className="text-[color:var(--ink-soft)]">
            {isAdmin ? t.galeria.folderEmptyAdmin : t.galeria.folderEmptyGuest}
          </p>
        </div>
      ) : (
        <MediaGrid
          items={items}
          onOpen={setViewerIdx}
          testid={`pro-grid-${folder.id}`}
          onDelete={isAdmin ? remove : null}
          paginate={true}
          pageSize={60}
          loadMoreLabel={t.galeria.loadMore}
          favoritesOnly={favoritesOnly}
          resetKey={folder.id}
        />
      )}

      {open && (
        <UploadModal
          t={t}
          onClose={() => setOpen(false)}
          onSaved={load}
          adminMode={true}
          category="profesionales"
          folder={folder.id}
        />
      )}
      <Viewer items={items} index={viewerIdx} onClose={() => setViewerIdx(null)} onChange={setViewerIdx} />
    </div>
  );
}

// --- Surprises ---
function SurprisesGallery({ t }) {
  const { user } = useAuth();
  const { lang } = useI18n();
  const { demoOn, now: clockNow } = useClock();
  const notif = useSurpriseNotif();
  const [surprises, setSurprises] = useState([]);
  const [openSurprise, setOpenSurprise] = useState(null);
  const [items, setItems] = useState({});
  const [viewerCtx, setViewerCtx] = useState({ items: [], index: null });
  const [uploadFor, setUploadFor] = useState(null);
  const [surpriseFavoritesOnly, setSurpriseFavoritesOnly] = useState(false);
  const [notifEnabled, setNotifEnabled] = useState(
    typeof Notification !== "undefined" && Notification.permission === "granted"
  );

  const loadSurprises = async () => {
    try {
      // In demo mode, always use public endpoint with demo_time so the preview reflects the
      // chosen moment (locked vs unlocked). Outside demo: admin uses /all to manage everything.
      let list;
      if (demoOn) {
        list = (await api.get("/surprises", { params: { demo_time: clockNow.toISOString() } })).data;
      } else {
        list = user ? (await api.get("/surprises/all")).data : (await api.get("/surprises")).data;
      }
      setSurprises(list || []);
    } catch {}
  };

  const loadMedia = async (surprise_id) => {
    try {
      const { data } = await api.get("/gallery", { params: { category: "sorpresa", surprise_id } });
      setItems((s) => ({ ...s, [surprise_id]: data }));
    } catch {}
  };

  useEffect(() => { loadSurprises(); }, [user, demoOn]);
  // Preload media counts for the 4 surprise folders so the badges show as soon as user lands
  useEffect(() => {
    if (surprises.length === 0) return;
    const preload = ["Vídeo 360º en el aperitivo", "Fotomatón", "Entrada especial al aperitivo", "Detalles para vosotros"];
    for (const title of preload) {
      const s = surprises.find((x) => x.title === title);
      if (s && !items[s.id]) loadMedia(s.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surprises]);
  // While in demo mode, refresh quickly so jumping the clock reflects locked/unlocked at a glance
  useEffect(() => {
    if (!demoOn) return undefined;
    const id = setInterval(() => loadSurprises(), 3000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [demoOn]);

  const toggleUnlock = async (s) => {
    try {
      await api.patch(`/surprises/${s.id}`, { unlocked: !s.unlocked });
      toast.success(s.unlocked ? "Ocultada" : "Revelada ✿");
      loadSurprises();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const removeMedia = async (id, surprise_id) => {
    if (!window.confirm("¿Eliminar?")) return;
    try { await api.delete(`/gallery/${id}`); toast.success("Eliminada"); loadMedia(surprise_id); } catch (e) { toast.error(formatApiError(e)); }
  };

  const enableNotif = async () => {
    const p = await notif.requestPermission();
    setNotifEnabled(p === "granted");
  };

  // 4 hardcoded folders mapped to specific surprises by their canonical Spanish title
  const SURPRISE_FOLDERS = [
    { key: "video360", title: "Video 360", titleCa: "Vídeo 360", icon: "🎥", surpriseTitle: "Vídeo 360º en el aperitivo" },
    { key: "fotomaton", title: "Fotomatón", titleCa: "Fotomaton", icon: "📸", surpriseTitle: "Fotomatón" },
    { key: "entrada-aperitivo", title: "Entrada aperitivo", titleCa: "Entrada aperitiu", icon: "🎆", surpriseTitle: "Entrada especial al aperitivo" },
    { key: "detalles", title: "Detalles", titleCa: "Detalls", icon: "🎁", surpriseTitle: "Detalles para vosotros" },
  ];

  const folderSurprises = SURPRISE_FOLDERS
    .map((f) => ({ ...f, surprise: surprises.find((s) => s.title === f.surpriseTitle) }))
    .filter((f) => f.surprise); // only show folders whose surprise exists in DB

  const openFolder = folderSurprises.find((f) => f.surprise.id === openSurprise);

  const goBack = () => { setOpenSurprise(null); };

  return (
    <div className="max-w-4xl mx-auto" data-testid="surprises-folders">
      {!openSurprise ? (
        <>
          <p className="text-center text-[color:var(--ink-soft)] mb-6 text-sm" data-testid="surprises-header">
            {t.galeria.surprisesFoldersHint}
          </p>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
            {folderSurprises.map((f) => {
              const surprise = f.surprise;
              const count = items[surprise.id]?.length ?? null;
              return (
                <button
                  key={f.key}
                  onClick={() => { loadMedia(surprise.id); setOpenSurprise(surprise.id); }}
                  className="s-card s-card-pad text-center transition-all hover:shadow-lg hover:-translate-y-0.5 group"
                  data-testid={`surprise-folder-${f.key}`}
                >
                  <div className="text-4xl sm:text-5xl mb-2 transition-transform group-hover:scale-110">{f.icon}</div>
                  <h3 className="font-script-alt text-2xl text-[color:var(--ink-wine)] mb-1">
                    {lang === "ca" ? f.titleCa : f.title}
                  </h3>
                  {count !== null && (
                    <p className="text-xs tracking-[0.18em] uppercase font-bold text-[color:var(--olive-deep)]">
                      {count} {count === 1 ? t.galeria.singleCountShort : t.galeria.pluralCountShort}
                    </p>
                  )}
                </button>
              );
            })}
          </div>

          {folderSurprises.length === 0 && (
            <p className="text-center text-[color:var(--ink-soft)] italic mt-8">{t.galeria.noSurprisesYet}</p>
          )}
        </>
      ) : (
        <div data-testid={`surprise-folder-view-${openFolder?.key}`}>
          <div className="flex items-center justify-between gap-3 mb-4">
            <button onClick={goBack} className="btn-outline !px-3" data-testid="surprise-folder-back">
              <ChevronLeft size={14} /> {t.galeria.backToFolders}
            </button>
            <div className="text-center flex-1">
              <p className="font-script-alt text-3xl text-[color:var(--ink-wine)] leading-none">
                {openFolder?.icon} {lang === "ca" ? openFolder?.titleCa : openFolder?.title}
              </p>
              <p className="text-[0.65rem] tracking-[0.28em] uppercase font-bold text-[color:var(--ink-soft)] mt-1">
                {(items[openSurprise]?.length || 0)} {(items[openSurprise]?.length || 0) === 1 ? t.galeria.singleCountShort : t.galeria.pluralCountShort}
              </p>
            </div>
            {user ? (
              <button onClick={() => setUploadFor(openFolder.surprise)} className="btn-primary !px-3" data-testid={`surprise-upload-${openFolder.key}`}>
                <Plus size={14} /> <span className="hidden sm:inline">{t.galeria.proUploadShort}</span>
              </button>
            ) : (
              <div className="w-[80px]" />
            )}
          </div>

          {(items[openSurprise]?.length || 0) > 0 && (
            <div className="flex justify-center mb-6">
              <button
                type="button"
                onClick={() => setSurpriseFavoritesOnly((v) => !v)}
                className={`inline-flex items-center gap-2 px-4 h-9 rounded-full text-xs font-semibold tracking-[0.18em] uppercase transition border ${surpriseFavoritesOnly ? "bg-[color:var(--ink-wine)] text-white border-[color:var(--ink-wine)]" : "border-[color:var(--line)] text-[color:var(--ink-wine)] hover:border-[color:var(--ink-wine)]"}`}
                data-testid={`favorites-toggle-surprise-${openFolder?.key}`}
                aria-pressed={surpriseFavoritesOnly}
              >
                <Heart size={13} fill={surpriseFavoritesOnly ? "currentColor" : "none"} />
                {surpriseFavoritesOnly ? "Mostrando favoritos" : "Solo con corazón"}
              </button>
            </div>
          )}

          {!items[openSurprise] || items[openSurprise].length === 0 ? (
            <div className="s-card s-card-pad text-center max-w-xl mx-auto">
              <Camera className="mx-auto text-[color:var(--ink-wine)] mb-3" size={42} />
              <p className="font-script-alt text-2xl text-[color:var(--ink-wine)] mb-2">{t.galeria.folderEmpty}</p>
              <p className="text-[color:var(--ink-soft)]">
                {user ? t.galeria.folderEmptyAdmin : t.galeria.folderEmptyGuest}
              </p>
            </div>
          ) : (
            <MediaGrid
              items={items[openSurprise]}
              onOpen={(idx) => setViewerCtx({ items: items[openSurprise], index: idx })}
              testid={`grid-surprise-${openSurprise}`}
              onDelete={user ? (id) => removeMedia(id, openSurprise) : null}
              paginate={true}
              pageSize={60}
              loadMoreLabel={t.galeria.loadMore}
              favoritesOnly={surpriseFavoritesOnly}
              resetKey={openSurprise}
            />
          )}
        </div>
      )}

      {uploadFor && (
        <UploadModal
          t={t}
          onClose={() => setUploadFor(null)}
          onSaved={() => { loadMedia(uploadFor.id); }}
          adminMode={true}
          category="sorpresa"
          surpriseId={uploadFor.id}
        />
      )}
      <Viewer
        items={viewerCtx.items}
        index={viewerCtx.index}
        onClose={() => setViewerCtx({ items: [], index: null })}
        onChange={(idx) => setViewerCtx((c) => ({ ...c, index: idx }))}
      />
    </div>
  );
}
function UploadModal({ t, onClose, onSaved, adminMode, category, surpriseId, folder }) {
  const [author, setAuthor] = useState(adminMode ? "Selena & Mario" : getGuestName());
  const [caption, setCaption] = useState("");
  const [queue, setQueue] = useState([]); // [{ file, id, status: pending|uploading|done|error, progress, error? }]
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const maxFiles = adminMode ? 500 : 1;

  const onFile = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    if (!adminMode && files.length > 1) {
      toast.info(t.galeria.singleOnlyGuest || "Solo puedes subir 1 archivo cada vez");
    }
    const trimmed = files.slice(0, maxFiles);
    const items = trimmed.map((f) => ({
      file: f,
      id: `${f.name}-${f.size}-${Math.random().toString(36).slice(2, 8)}`,
      status: "pending",
      progress: 0,
      preview: f.type.startsWith("image/") ? URL.createObjectURL(f) : null,
    }));
    setQueue((q) => (adminMode ? [...q, ...items] : items));
    e.target.value = "";
  };

  const removeItem = (id) => {
    setQueue((q) => {
      const found = q.find((x) => x.id === id);
      if (found?.preview) URL.revokeObjectURL(found.preview);
      return q.filter((x) => x.id !== id);
    });
  };

  const uploadOne = async (item) => {
    const fd = new FormData();
    fd.append("file", item.file);
    fd.append("author", author.trim() || "Anónimo");
    if (caption) fd.append("caption", caption);
    fd.append("category", category);
    if (surpriseId) fd.append("surprise_id", surpriseId);
    if (folder) fd.append("folder", folder);
    const url = adminMode ? "/gallery/upload-admin" : "/gallery/upload";
    // Retry on transient errors (503/504/520/522/524/network) up to 3 times with backoff.
    const RETRIABLE = new Set([408, 429, 500, 502, 503, 504, 520, 521, 522, 524]);
    let lastErr;
    for (let attempt = 1; attempt <= 3; attempt += 1) {
      try {
        return await api.post(url, fd, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 180000, // 3 minutes for big files
          onUploadProgress: (evt) => {
            const pct = evt.total ? Math.round((evt.loaded / evt.total) * 100) : 0;
            setQueue((q) => q.map((x) => (x.id === item.id ? { ...x, progress: pct, status: "uploading" } : x)));
          },
        });
      } catch (err) {
        lastErr = err;
        const status = err?.response?.status;
        const retriable = !status || RETRIABLE.has(status);
        if (!retriable || attempt === 3) throw err;
        // Exponential backoff: 2s, 6s
        const wait = attempt === 1 ? 2000 : 6000;
        setQueue((q) => q.map((x) => (x.id === item.id ? { ...x, status: "uploading", progress: 0, error: `Reintentando (${attempt}/3)…` } : x)));
        await new Promise((r) => setTimeout(r, wait));
      }
    }
    throw lastErr;
  };

  const submit = async (e) => {
    e?.preventDefault();
    if (queue.length === 0) { toast.error(t.common.fileRequired); return; }
    if (!author.trim()) { toast.error(t.common.nameRequired); return; }
    setBusy(true);
    // Serial upload — the backend generates a thumbnail (CPU) and pushes to object storage for
    // every file, so parallel uploads of large photos saturate the origin and trigger Cloudflare
    // 520/503 errors. Serial with retries is far more reliable for 200+ file batches.
    let successes = 0;
    let failures = 0;

    for (let i = 0; i < queue.length; i += 1) {
      const my = queue[i];
      if (my.status === "done") { successes += 1; continue; }
      setQueue((q) => q.map((x) => (x.id === my.id ? { ...x, status: "uploading", progress: 0, error: undefined } : x)));
      try {
        await uploadOne(my);
        setQueue((q) => q.map((x) => (x.id === my.id ? { ...x, status: "done", progress: 100, error: undefined } : x)));
        successes += 1;
      } catch (err) {
        setQueue((q) => q.map((x) => (x.id === my.id ? { ...x, status: "error", error: formatApiError(err) } : x)));
        failures += 1;
      }
    }
    if (!adminMode) setGuestName(author);
    if (successes > 0) toast.success(`${successes} ${successes === 1 ? "archivo subido" : "archivos subidos"}`);
    if (failures > 0) toast.error(`${failures} con error`);
    setBusy(false);
    onSaved();
    if (failures === 0) onClose();
  };

  const totalDone = queue.filter((x) => x.status === "done").length;
  const totalUploading = queue.filter((x) => x.status === "uploading").length;
  const overallPct = queue.length > 0 ? Math.round((totalDone / queue.length) * 100) : 0;

  return (
    <div className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={busy ? undefined : onClose} data-testid="upload-modal">
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="s-card s-card-pad w-full max-w-2xl relative max-h-[90vh] overflow-y-auto">
        <button type="button" onClick={onClose} disabled={busy} className="absolute top-3 right-3 p-1 text-[color:var(--ink-wine)] disabled:opacity-30" aria-label="Cerrar"><X size={20} /></button>
        <h3 className="font-script-alt text-3xl text-[color:var(--ink-wine)] mb-1">{t.galeria.uploadBtn}</h3>
        {adminMode && (
          <p className="text-xs text-[color:var(--ink-soft)] mb-3">
            {t.galeria.bulkHint || `Puedes seleccionar varias fotos o vídeos a la vez (hasta ${maxFiles}). Máx. 100 MB por archivo.`}
          </p>
        )}

        <div className="space-y-4 mt-2">
          <div>
            <label className="label">{t.galeria.yourName}</label>
            <input data-testid="upload-author" value={author} onChange={(e) => setAuthor(e.target.value)} className="field" required disabled={busy} />
          </div>
          {!adminMode && (
            <div>
              <label className="label">{t.galeria.caption}</label>
              <input data-testid="upload-caption" value={caption} onChange={(e) => setCaption(e.target.value)} className="field" disabled={busy} />
            </div>
          )}
          <div>
            <input
              ref={inputRef}
              type="file"
              accept="image/*,video/*"
              multiple={adminMode}
              className="hidden"
              onChange={onFile}
              data-testid="upload-file"
              disabled={busy}
            />
            <button type="button" onClick={() => inputRef.current?.click()} className="btn-outline w-full" disabled={busy}>
              <Upload size={14} /> {adminMode ? (t.galeria.pickMany || "Elegir fotos / vídeos") : (queue.length ? t.galeria.change : t.galeria.pickFile)}
            </button>

            {queue.length > 0 && (
              <div className="mt-3 space-y-2 max-h-72 overflow-y-auto pr-1" data-testid="upload-queue">
                {adminMode && queue.length > 1 && (
                  <div className="flex items-center gap-3 mb-1">
                    <div className="flex-1 h-1.5 bg-[color:var(--bg-blush)] rounded-full overflow-hidden">
                      <div className="h-full bg-[color:var(--olive-deep)] transition-all" style={{ width: `${overallPct}%` }} />
                    </div>
                    <span className="text-xs font-semibold text-[color:var(--ink-wine)] shrink-0">
                      {totalDone}/{queue.length}{totalUploading > 0 ? " ·" : ""}
                    </span>
                  </div>
                )}
                {queue.map((q) => (
                  <div key={q.id} className="flex items-center gap-3 p-2 rounded-lg bg-[color:var(--bg-blush)]/60 border border-[color:var(--line)]" data-testid={`upload-item-${q.status}`}>
                    {q.preview ? (
                      <img src={q.preview} alt="" className="w-10 h-10 rounded object-cover" />
                    ) : (
                      <span className="w-10 h-10 rounded bg-white flex items-center justify-center text-[color:var(--ink-wine)]">
                        {q.file.type.startsWith("video/") ? <Play size={16} /> : <ImageIcon size={16} />}
                      </span>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold truncate text-[color:var(--ink-deep)]">{q.file.name}</p>
                      <p className="text-[0.65rem] text-[color:var(--ink-soft)]">{(q.file.size / 1024 / 1024).toFixed(2)} MB</p>
                      {q.status === "uploading" && (
                        <>
                          <div className="h-1 bg-[color:var(--bg-paper)] rounded-full mt-1 overflow-hidden">
                            <div className="h-full bg-[color:var(--ink-wine)]" style={{ width: `${q.progress}%` }} />
                          </div>
                          {q.error && (
                            <p className="text-[0.65rem] text-amber-700 truncate mt-0.5">{q.error}</p>
                          )}
                        </>
                      )}
                      {q.status === "error" && <p className="text-[0.65rem] text-red-700 truncate">{q.error}</p>}
                    </div>
                    {q.status === "done" && <span className="text-green-700 text-lg">✓</span>}
                    {q.status === "error" && <span className="text-red-700 text-lg">✗</span>}
                    {q.status === "pending" && !busy && (
                      <button type="button" onClick={() => removeItem(q.id)} className="text-[color:var(--ink-soft)] hover:text-red-700 p-1" aria-label="Quitar">
                        <X size={14} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <button type="submit" disabled={busy || queue.length === 0} className="btn-primary mt-5" data-testid="upload-submit">
          {busy
            ? `${t.galeria.submitting} ${totalDone}/${queue.length}`
            : (queue.length > 1 ? `${t.galeria.submit} (${queue.length})` : t.galeria.submit)}
        </button>
      </form>
    </div>
  );
}
